/**
 * useDossier — polling hook for the AI destination dossier of a single trip.
 *
 * Strategy: one query keyed by trip id that polls every 3s while the latest
 * dossier is `pending`, then stops once status flips to `ready` or `error`.
 * The generate mutation kicks off a new pending row server-side; the polling
 * query is invalidated so the UI immediately reflects the new pending state.
 *
 * Feature-flagged: when `dossier` is disabled the query is skipped entirely,
 * so switching the feature off truly stops all network traffic.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type { TravelDossierResponse } from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

const POLL_INTERVAL_MS = 3000;

export function useDossier(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const dossierFeature = useTravelFeature('dossier');
  const key = queryKeys.travel.dossier(tripId ?? '');

  const enabled = Boolean(tripId) && dossierFeature.enabled;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => travelApi.getLatestTripDossier(tripId!),
    enabled,
    refetchInterval: (query) => {
      const latest = query.state.data?.dossier;
      if (latest?.status === 'pending') return POLL_INTERVAL_MS;
      return false;
    },
  });

  const generateMut = useMutation({
    mutationFn: () => travelApi.generateTripDossier(tripId!),
    onSuccess: (res) => {
      // Seed the cache with the new pending row so polling starts immediately.
      queryClient.setQueryData<{ dossier: TravelDossierResponse | null } | undefined>(key, {
        dossier: res.dossier,
      });
    },
  });

  return {
    dossier: data?.dossier ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    isGenerating: generateMut.isPending,
    /**
     * True whenever the latest known dossier is still being generated —
     * either because the user just hit Generate or a prior run hasn't
     * finished. The UI should show a loading state in both cases.
     */
    isPending: data?.dossier?.status === 'pending' || generateMut.isPending,
    generate: () => generateMut.mutateAsync(),
    refresh: refetch,
    featureEnabled: dossierFeature.enabled,
  };
}
